import {
  parseISO,
  parse,
  isValid,
  formatISO,
  isToday,
  isYesterday,
  isTomorrow,
  isThisWeek,
  isThisYear,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  fromUnixTime,
  getUnixTime,
  isBefore
} from "date-fns";

import { toZonedTime, fromZonedTime, formatInTimeZone } from "date-fns-tz";

// Common date formats used throughout the application
export const DATE_FORMATS = {
  // Date only formats
  DATE_ONLY: "yyyy-MM-dd",
  DATE_DISPLAY: "dd MMM yyyy",
  /** e.g. Apr 11, 2026 — used where US-style month-first reads better (shift cards, summaries). */
  MONTH_DAY_COMMA_YEAR: "MMM dd, yyyy",
  DATE_SHORT: "dd/MM/yyyy",
  DATE_LONG: "EEEE, MMMM d, yyyy",

  // Date and time formats
  DATETIME_DISPLAY: "MMM dd, yyyy h:mm a",

  // Time only formats
  TIME_ONLY: "HH:mm",

  // Special formats
  DAY_MONTH: "dd MMM",
  /** e.g. Aug - a bare month, for axes that already establish the year. */
  MONTH_SHORT: "MMM",
  WEEKDAY: "EEEE"
} as const;

// Type definitions for better type safety
export type DateInput = Date | string | number;
export type DateFormat = keyof typeof DATE_FORMATS;

// Timezone utilities
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getUTCTimezone = (): string => {
  return "UTC";
};

/**
 * Core date formatting utilities
 */
class DateUtils {
  /**
   * Format a date to a specific format in user's timezone
   */
  static formatDate(
    date: DateInput,
    formatKey: DateFormat = "DATE_DISPLAY",
    timezone?: string
  ): string {
    const dateObj = this.parseDate(date);
    if (!isValid(dateObj)) {
      throw new Error(`Invalid date: ${date}`);
    }

    const targetTimezone = timezone || getUserTimezone();
    const formatString = DATE_FORMATS[formatKey];

    // If the date is already in UTC, format it in the user's timezone
    return formatInTimeZone(dateObj, targetTimezone, formatString);
  }

  /**
   * Format a date in UTC (for API/storage)
   */
  static formatDateUTC(
    date: DateInput,
    formatKey: DateFormat = "DATE_DISPLAY"
  ): string {
    const dateObj = this.parseDate(date);
    if (!isValid(dateObj)) {
      throw new Error(`Invalid date: ${date}`);
    }

    return formatInTimeZone(dateObj, "UTC", DATE_FORMATS[formatKey]);
  }

  /**
   * Format date only (without time) in user's timezone
   */
  static formatDateOnly(
    date: DateInput,
    formatKey: DateFormat = "DATE_DISPLAY",
    timezone?: string
  ): string {
    return this.formatDate(date, formatKey, timezone);
  }

  /**
   * Format date and time in user's timezone
   */
  static formatDateTime(
    date: DateInput,
    formatKey: DateFormat = "DATETIME_DISPLAY",
    timezone?: string
  ): string {
    return this.formatDate(date, formatKey, timezone);
  }

  /**
   * Format time only in user's timezone
   */
  static formatTimeOnly(
    date: DateInput,
    formatKey: DateFormat = "TIME_ONLY",
    timezone?: string
  ): string {
    return this.formatDate(date, formatKey, timezone);
  }

  /**
   * Parse a date string to Date object (assumes UTC input)
   */
  static parseDate(date: DateInput): Date {
    if (date instanceof Date) {
      return date;
    }

    if (typeof date === "string") {
      // Try parsing ISO string first (handles UTC automatically)
      if (date.includes("T") || date.includes("Z")) {
        return parseISO(date);
      }

      // Time-only strings (e.g. shift API returns "09:00:00" for start/end)
      if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(date.trim())) {
        const s = date.trim();
        const ref = new Date(2000, 0, 1);
        const timeFormats = ["HH:mm:ss", "H:mm:ss", "HH:mm", "H:mm"] as const;
        for (const fmt of timeFormats) {
          const parsed = parse(s, fmt, ref);
          if (isValid(parsed)) {
            return parsed;
          }
        }
      }

      // For date-only strings, assume UTC and convert to local time for parsing
      const formats = [
        "yyyy-MM-dd",
        "dd/MM/yyyy",
        "dd-MM-yyyy",
        "MM/dd/yyyy",
        "yyyy/MM/dd",
        "dd MMM yyyy",
        "MMM dd, yyyy"
      ];

      for (const formatStr of formats) {
        try {
          // Parse as UTC first, then convert to local timezone
          const parsed = parse(date, formatStr, new Date());
          if (isValid(parsed)) {
            // Ensure the date is treated as UTC
            return new Date(
              parsed.getTime() - parsed.getTimezoneOffset() * 60000
            );
          }
        } catch {
          // Continue to next format
        }
      }

      // Fallback to native Date parsing
      return new Date(date);
    }

    if (typeof date === "number") {
      // Unix timestamp (already UTC)
      return fromUnixTime(date);
    }

    throw new Error(`Unable to parse date: ${date}`);
  }

  /**
   * Parse a date string assuming it's in user's timezone
   */
  static parseDateInTimezone(date: DateInput, timezone?: string): Date {
    const targetTimezone = timezone || getUserTimezone();

    if (date instanceof Date) {
      return date;
    }

    if (typeof date === "string") {
      // Parse the date in the specified timezone
      const parsed = parse(date, "yyyy-MM-dd", new Date());
      if (isValid(parsed)) {
        // Convert from user timezone to UTC
        return fromZonedTime(parsed, targetTimezone);
      }
    }

    return this.parseDate(date);
  }

  /**
   * Parse date only string (yyyy-MM-dd format)
   */
  static parseDateOnly(dateString: string): Date {
    const parsed = parse(dateString, "yyyy-MM-dd", new Date());
    if (!isValid(parsed)) {
      throw new Error(`Invalid date string: ${dateString}`);
    }
    return parsed;
  }

  /**
   * Parse date and time string
   */
  static parseDateTime(dateTimeString: string): Date {
    return this.parseDate(dateTimeString);
  }

  /**
   * Convert date to ISO string (UTC)
   */
  static toISOString(date: DateInput): string {
    const dateObj = this.parseDate(date);
    return formatISO(dateObj);
  }

  /**
   * Convert date to Unix timestamp (UTC)
   */
  static toUnixTimestamp(date: DateInput): number {
    const dateObj = this.parseDate(date);
    return getUnixTime(dateObj);
  }

  /**
   * Convert UTC date to user's timezone
   */
  static toUserTimezone(date: DateInput, timezone?: string): Date {
    const dateObj = this.parseDate(date);
    const targetTimezone = timezone || getUserTimezone();
    return toZonedTime(dateObj, targetTimezone);
  }

  /**
   * Convert user timezone date to UTC
   */
  static toUTC(date: DateInput, timezone?: string): Date {
    const dateObj = this.parseDate(date);
    const sourceTimezone = timezone || getUserTimezone();
    return fromZonedTime(dateObj, sourceTimezone);
  }

  /**
   * Check if date is valid
   */
  static isValidDate(date: DateInput): boolean {
    try {
      const dateObj = this.parseDate(date);
      return isValid(dateObj);
    } catch {
      return false;
    }
  }

  /**
   * Relative date formatting (today, yesterday, etc.)
   */
  static formatRelative(date: DateInput): string {
    const dateObj = this.parseDate(date);

    if (isToday(dateObj)) {
      return "Today";
    }

    if (isYesterday(dateObj)) {
      return "Yesterday";
    }

    if (isTomorrow(dateObj)) {
      return "Tomorrow";
    }

    if (isThisWeek(dateObj)) {
      return this.formatDate(dateObj, "WEEKDAY");
    }

    if (isThisYear(dateObj)) {
      return this.formatDate(dateObj, "DAY_MONTH");
    }

    return this.formatDate(dateObj, "DATE_DISPLAY");
  }

  /**
   * Get time until date
   */
  static getTimeUntil(date: DateInput): string {
    const dateObj = this.parseDate(date);
    const now = new Date();

    const days = differenceInDays(dateObj, now);
    const hours = differenceInHours(dateObj, now);
    const minutes = differenceInMinutes(dateObj, now);

    if (days > 0) {
      return `${days} day${days === 1 ? "" : "s"} remaining`;
    }

    if (hours > 0) {
      return `${hours} hour${hours === 1 ? "" : "s"} remaining`;
    }

    if (minutes > 0) {
      return `${minutes} minute${minutes === 1 ? "" : "s"} remaining`;
    }

    return "Now";
  }

  /**
   * Get time since date
   */
  static getTimeSince(date: DateInput): string {
    const dateObj = this.parseDate(date);
    const now = new Date();

    const days = differenceInDays(now, dateObj);
    const hours = differenceInHours(now, dateObj);
    const minutes = differenceInMinutes(now, dateObj);

    if (days > 0) {
      return `${days} day${days === 1 ? "" : "s"} ago`;
    }

    if (hours > 0) {
      return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    }

    if (minutes > 0) {
      return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    }

    return "Just now";
  }

  /**
   * Check if start time is before end time
   */
  static isStartBeforeEnd(startTime: string, endTime: string) {
    const referenceDate = new Date();

    const startDate = parse(startTime, "H:mm", referenceDate);
    const endDate = parse(endTime, "H:mm", referenceDate);

    return isBefore(startDate, endDate);
  }
}

// Convenience functions for common operations
export const formatDate = (
  date: DateInput,
  formatKey: DateFormat = "DATE_DISPLAY",
  timezone?: string
): string => {
  try {
    return DateUtils.formatDate(date, formatKey, timezone);
  } catch (error) {
    console.error(error);
    return "";
  }
};

// UTC formatting functions
export const formatDateUTC = (
  date: DateInput,
  formatKey: DateFormat = "DATE_DISPLAY"
): string => {
  return DateUtils.formatDateUTC(date, formatKey);
};

// Timezone conversion functions
export const toUserTimezone = (date: DateInput, timezone?: string): Date => {
  return DateUtils.toUserTimezone(date, timezone);
};

export const toUTC = (date: DateInput, timezone?: string): Date => {
  return DateUtils.toUTC(date, timezone);
};

export const parseDate = (date: DateInput): Date => {
  return DateUtils.parseDate(date);
};

export const toISOString = (date: DateInput): string => {
  return DateUtils.toISOString(date);
};

export const toUnixTimestamp = (date: DateInput): number => {
  return DateUtils.toUnixTimestamp(date);
};

export const isValidDate = (date: DateInput): boolean => {
  return DateUtils.isValidDate(date);
};

export const formatRelative = (date: DateInput): string => {
  return DateUtils.formatRelative(date);
};

export const getTimeUntil = (date: DateInput): string => {
  return DateUtils.getTimeUntil(date);
};

export const getTimeSince = (date: DateInput): string => {
  return DateUtils.getTimeSince(date);
};

export const isStartBeforeEnd = (
  startTime: string,
  endTime: string
): boolean => {
  return DateUtils.isStartBeforeEnd(startTime, endTime);
};

// Export the class and formats for advanced usage
export { DateUtils };

/** Convert "HH:mm" 24-hour string to "h:mm am/pm". */
export function formatTime12h(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, "0")} ${ampm}`;
}

/** Duration in minutes between two "HH:mm" time strings on the same day. */
export function timeDurationMinutes(startTime: string, endTime: string): number {
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  return eh * 60 + em - (sh * 60 + sm);
}

/** Format a minute count as "Xm", "Xh", or "Xh Ym". */
export function formatDuration(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Compute a human-readable duration string from two "HH:mm" time strings.
 * Returns null when inputs are missing or the duration is non-positive.
 */
export function computeHoursWorked(
  startTime: string | null,
  endTime: string | null
): string | null {
  if (!startTime || !endTime) return null;
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  if (isNaN(sh) || isNaN(sm) || isNaN(eh) || isNaN(em)) return null;
  const diff = eh * 60 + em - (sh * 60 + sm);
  if (diff <= 0) return null;
  return formatDuration(diff);
}

/**
 * Build a calendar date range from filter `yyyy-MM-dd` strings.
 * Prefer deriving UI range from filter props over mirroring them in effects.
 */
export function parseFilterDateRange(
  startDate?: string | null,
  endDate?: string | null
): { from: Date; to: Date } | undefined {
  if (!startDate) return undefined;
  const from = parse(startDate, DATE_FORMATS.DATE_ONLY, new Date());
  if (!isValid(from)) return undefined;
  const to = endDate
    ? parse(endDate, DATE_FORMATS.DATE_ONLY, new Date())
    : from;
  return { from, to: isValid(to) ? to : from };
}
